// RigSentinel — Drilling Fluid Management (Phase RCE-3)
//
// MUD PROGRAM HUB · /wells/:wellId/mud-programs
// Lists every Mud Program owned by a single well.

import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Plus, ArrowLeft, Trash2, FlaskConical } from "lucide-react";
import { getWellProfile } from "@/lib/well/storage";
import { wellStackMode } from "@/lib/well/schema";
import {
  listMudProgramsForWell, deleteMudProgram, MUD_PROGRAM_STATUSES,
} from "@/lib/mud/program";
import { useStackMode } from "@/lib/stackModeContext";
import { stackAccentColor } from "@/lib/stackAccent";

const STATUS_COLOR = {
  draft:    "border-zinc-500/40 text-zinc-300",
  approved: "border-emerald-500/40 text-emerald-300",
  "in-use": "border-amber-500/50 text-amber-400",
  archived: "border-zinc-700/40 text-zinc-500",
};

export default function MudProgramHub() {
  const { stackMode } = useStackMode();
  const navigate = useNavigate();
  const { wellId } = useParams();
  const [well, setWell] = useState(() => getWellProfile(wellId));
  const [rows, setRows] = useState(() => listMudProgramsForWell(wellId));

  const refresh = () => {
    setWell(getWellProfile(wellId));
    setRows(listMudProgramsForWell(wellId));
  };

  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [wellId]);

  if (!well) {
    return (
      <div className="card-rs p-6 text-sm text-[var(--rs-text-3)]">
        Well not found. <Link to="/wells" className="text-amber-400">Back to Wells</Link>.
      </div>
    );
  }

  // Surface/Subsea isolation — see KillSheetOpHub for rationale.
  if (wellStackMode(well) !== stackMode) {
    return (
      <div className="card-rs p-6 text-sm text-[var(--rs-text-3)]">
        &quot;{well.wellName || "This well"}&quot; belongs to {wellStackMode(well) === "subsea" ? "Subsea" : "Surface"} mode.
        Switch the operating-mode toggle back to view its mud programs, or{" "}
        <Link to="/wells" className="text-amber-400">go back to Wells</Link>.
      </div>
    );
  }

  const handleDelete = (id, name) => {
    if (!window.confirm(`Delete Mud Program "${name}"?`)) return;
    deleteMudProgram(id);
    refresh();
  };

  return (
    <div data-testid="mud-program-hub" className="flex flex-col gap-4 pb-8">
      <header className="flex items-center justify-between gap-3">
        <Link
          to={`/wells/${wellId}`}
          data-testid="mud-program-hub-back"
          className="inline-flex items-center gap-1.5 text-[12px] text-[var(--rs-text-2)] hover:text-[var(--rs-amber)]"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> {well.wellName || "Well"}
        </Link>
        <Link
          to={`/wells/${wellId}/mud-programs/new`}
          data-testid="mud-program-hub-new"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium"
          style={{ background: stackAccentColor(stackMode), color: "var(--rs-bg)" }}
        >
          <Plus className="w-3.5 h-3.5" /> New Mud Program
        </Link>
      </header>

      <div>
        <h1 className="font-heading font-black text-xl flex items-center gap-2">
          <FlaskConical className="w-5 h-5" style={{ color: stackAccentColor(stackMode) }} />
          Mud Program
        </h1>
        <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--rs-text-3)]">
          Drilling Fluid Management · RCE-1.0
        </div>
      </div>

      {rows.length === 0 ? (
        <div
          data-testid="mud-program-empty"
          className="card-rs p-6 text-center text-[13px] text-[var(--rs-text-2)]"
        >
          No Mud Programs yet for this well. Create one to define planned mud properties.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="mud-program-list">
          {rows.map((p) => (
            <li
              key={p.id}
              data-testid={`mud-program-row-${p.id}`}
              className="card-rs p-3 flex items-start gap-3"
            >
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-[var(--rs-text-1)]">{p.name}</div>
                <div className="text-[10.5px] text-[var(--rs-text-3)] mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[9px] uppercase tracking-[0.16em] ${STATUS_COLOR[p.status] || ""}`}>{p.status}</span>
                  <span><b>Basis:</b> {p.fluidBasis}</span>
                  <span><b>Intervals:</b> {p.intervals?.length || 0}</span>
                </div>
              </div>
              <button
                onClick={() => navigate(`/wells/${wellId}/mud-programs/${p.id}`)}
                className="btn-ghost h-8 px-2 rounded text-[11px] label-up"
                data-testid={`mud-program-edit-${p.id}`}
              >
                Edit
              </button>
              <button
                onClick={() => handleDelete(p.id, p.name)}
                className="btn-ghost h-8 w-8 rounded inline-flex items-center justify-center"
                data-testid={`mud-program-delete-${p.id}`}
              >
                <Trash2 className="w-3.5 h-3.5 text-rose-400" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
